import type {
  BurnInPolygon,
  BurnInRect,
  BurnInWatermark,
  BurnInWorkerResponse,
} from './burnInWorker';

/**
 * Thin promise-based wrapper around the burn-in Web Worker.
 *
 * - Spawns the worker lazily on the first burn-in request, so the ~10 MB
 *   mupdf WASM only loads when the user actually clicks Redact File.
 * - The worker is reused across saves within a single doc-editor session;
 *   the WASM stays warm.
 * - Multiple concurrent burn-ins are correlated by an auto-incrementing
 *   request id (matches `BurnInWorkerResponse.id`).
 * - Every request is bounded by `BURN_IN_TIMEOUT_MS` (VE-28061): mupdf can hang
 *   rather than throw, and an unbounded pending promise now strands a lock, not
 *   just a spinner. A timeout rejects the request and terminates the worker.
 * - A `fatal` error (WASM trap) also terminates it.
 */

export interface BurnInResult {
  pdfBytes: ArrayBuffer;
  numPages: number;
  /**
   * pages per part, in order, for a COMBINE. Absent for a burn-in.
   * The merged document's page ranges are derived from this, so a member can
   * later be found in the output without re-parsing it.
   */
  partPageCounts?: number[];
}

/**
 * Hard ceiling on a single burn-in (VE-28061).
 *
 * Nothing else bounded this: the worker-level `error` handler only fires for a
 * crash, so a mupdf job that hangs rather than throws left its promise pending
 * forever. That used to strand the editor's spinner; now it strands a lock —
 * `exportPush` refuses a fresh Redact File while the "Redacting" flag is set,
 * so a hang disabled the button for that document and pinned every list row
 * until a page reload.
 *
 * Ten minutes is far above any legitimate run (the slowest measured is a large
 * multi-hundred-page colour scan at well under a minute — see PERFORMANCE.md),
 * so firing means "stuck", not "slow".
 */
const BURN_IN_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Where a burn has got to (VE-31309), as the caller sees it.
 *
 * `total` is the number of pages carrying redactions, NOT the document's page
 * count — a 300-page scan with marks on four pages reports four. A caller
 * rendering this has to say what it is counting, or "page 4 of 4" reads as a
 * four-page document.
 */
export interface BurnInProgressReport {
  stage: 'redacting' | 'stamping' | 'finalizing';
  done: number;
  total: number;
  page: number | null;
  /** The document's page count, for "page 346 of 1602". */
  pageCount: number;
}

interface PendingRequest {
  resolve: (result: BurnInResult) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  onProgress?: (report: BurnInProgressReport) => void;
  /** Rearm the watchdog; a job that is reporting is not a job that is stuck. */
  restartTimer: () => void;
}

let worker: BurnInWorkerLike | null = null;
const pending = new Map<number, PendingRequest>();
let nextId = 1;

/** Settle a request exactly once, always clearing its timeout. */
function settle(id: number, apply: (handler: PendingRequest) => void): void {
  const handler = pending.get(id);
  if (!handler) {
    return;
  }
  clearTimeout(handler.timer);
  pending.delete(id);
  apply(handler);
}

/** Reject every in-flight request with one error (worker died or disposed). */
function rejectAllPending(message: string): void {
  for (const id of [...pending.keys()]) {
    settle(id, (handler) => handler.reject(new Error(message)));
  }
  pending.clear();
}

/**
 * How a worker gets made, so a test can make something else.
 *
 * `new Worker(new URL(...))` does not run under jsdom and the repo's jest
 * mapper only intercepts `worker-loader`, so without this seam nothing in
 * this file can be tested at all — including the watchdog, which is the part
 * that decides whether a long job is killed as stuck. The same seam exists on
 * `lexiconMatchWorkerClient` for the same reason.
 */
export type BurnInWorkerLike = Pick<
  Worker,
  'postMessage' | 'addEventListener' | 'terminate'
>;

let createWorker: () => BurnInWorkerLike = () =>
  new Worker(new URL('./burnInWorker.ts', import.meta.url), {
    type: 'module',
  });

/** TEST ONLY. Returns the previous factory so a test can put it back. */
export function setBurnInWorkerFactory(
  factory: () => BurnInWorkerLike
): () => BurnInWorkerLike {
  const previous = createWorker;
  createWorker = factory;
  return previous;
}

function getWorker(): BurnInWorkerLike {
  if (worker) {
    return worker;
  }
  const w = createWorker();
  w.addEventListener('message', (e: MessageEvent<BurnInWorkerResponse>) => {
    const res = e.data;
    /**
     * BEFORE `settle`, which deletes the entry. Progress is not a settlement:
     * the request is still in flight and must stay in `pending` to be
     * resolved later.
     *
     * It also REARMS THE WATCHDOG. That timeout exists to catch a job that
     * has hung, and it was a fixed ten minutes from the moment of asking —
     * so a genuinely long burn on a very large scan raced it and was killed
     * as stuck. A job that reported a page thirty seconds ago is not stuck,
     * and now the clock is "silent for ten minutes" rather than "running for
     * ten minutes".
     */
    if (res.type === 'BURN_IN_PROGRESS') {
      const handler = pending.get(res.id);
      if (handler) {
        handler.restartTimer();
        handler.onProgress?.({
          stage: res.stage,
          done: res.done,
          total: res.total,
          page: res.page,
          pageCount: res.pageCount,
        });
      }
      return;
    }
    settle(res.id, (handler) => {
      if (res.type === 'BURN_IN_SUCCESS') {
        handler.resolve({ pdfBytes: res.pdfBytes, numPages: res.numPages });
      } else if (res.type === 'COMBINE_SUCCESS') {
        handler.resolve({
          pdfBytes: res.pdfBytes,
          numPages: res.numPages,
          partPageCounts: res.partPageCounts,
        });
      } else {
        handler.reject(new Error(res.message));
      }
    });
    // After a WASM trap the heap may be corrupt; respawn on the next call.
    if (res.type === 'BURN_IN_ERROR' && res.fatal) {
      disposeBurnInWorker();
    }
  });
  w.addEventListener('error', (e) => {
    // Worker-level error (e.g. WASM failed to load) rejects every pending
    // request — they can't make forward progress on a dead worker.
    rejectAllPending(e.message || 'Burn-in worker crashed');
    worker = null;
  });
  worker = w;
  return w;
}

/**
 * One burn-in job's inputs.
 *
 * An OBJECT rather than positional parameters: this had grown to seven
 * positionals, at which point every call site is a row of bare arrays whose
 * meaning depends on counting commas, and adding an eighth (or reordering two
 * of the same type) is a silent defect. Every field but `pdfBytes` and
 * `redactions` is optional and defaults to empty.
 */
export interface BurnInOptions {
  pdfBytes: ArrayBuffer;
  redactions: BurnInRect[];
  /** Pages blanked to a slip sheet ("Remove Page"). */
  pageRedacts?: number[];
  /** Pages dropped from the output entirely ("Delete Page"). */
  excludedPages?: number[];
  allPagesRedactions?: Array<Omit<BurnInRect, 'page'>>;
  polygons?: BurnInPolygon[];
  /** Stuck-to-all-pages polygons; `polygons` is per-page and cannot carry them. */
  allPagesPolygons?: Array<Omit<BurnInPolygon, 'page'>>;
  /** Marks drawn on every surviving page, in draw order: watermarks and the Bates stamp. */
  watermarks?: BurnInWatermark[];
  /**
   * Called as the burn moves, zero or more times, never after it settles.
   * Advisory: dropping every call changes nothing but what is on screen.
   */
  onProgress?: (report: BurnInProgressReport) => void;
}

export function burnInPdf({
  pdfBytes,
  redactions,
  pageRedacts = [],
  excludedPages = [],
  allPagesRedactions = [],
  polygons = [],
  allPagesPolygons = [],
  watermarks = [],
  onProgress,
}: BurnInOptions): Promise<BurnInResult> {
  const id = nextId++;
  // Transfer any watermark-image ArrayBuffers alongside the PDF bytes so the
  // (potentially large base64-decoded) image payloads aren't structured-cloned.
  const transfer: ArrayBuffer[] = [pdfBytes];
  for (const wm of watermarks) {
    if (wm.kind === 'image') {
      transfer.push(wm.bytes);
    }
  }
  return new Promise<BurnInResult>((resolve, reject) => {
    // Terminate on timeout rather than only rejecting: the worker takes one job
    // at a time, so a hung job would keep every later burn-in queued behind it.
    // The next call lazily spawns a fresh worker.
    const expire = () => {
      settle(id, (handler) =>
        handler.reject(
          new Error(
            `Burn-in timed out after ${Math.round(
              BURN_IN_TIMEOUT_MS / 1000
            )}s (the document may be too complex to redact in the browser)`
          )
        )
      );
      disposeBurnInWorker();
    };
    const timer = setTimeout(expire, BURN_IN_TIMEOUT_MS);
    const entry: PendingRequest = {
      resolve,
      reject,
      timer,
      onProgress,
      restartTimer: () => {
        clearTimeout(entry.timer);
        entry.timer = setTimeout(expire, BURN_IN_TIMEOUT_MS);
      },
    };
    pending.set(id, entry);
    getWorker().postMessage(
      {
        id,
        type: 'BURN_IN' as const,
        pdfBytes,
        redactions,
        pageRedacts,
        excludedPages,
        allPagesRedactions,
        polygons,
        allPagesPolygons,
        watermarks,
      },
      transfer
    );
  });
}

/**
 * concatenate already-redacted PDFs into the merged document.
 */
export function combinePdfs(parts: ArrayBuffer[]): Promise<BurnInResult> {
  return new Promise<BurnInResult>((resolve, reject) => {
    if (!parts.length) {
      reject(new Error('Nothing to combine'));
      return;
    }
    const id = nextId;
    nextId += 1;
    const expire = () =>
      settle(id, (handler) => handler.reject(new Error('Combine timed out')));
    const timer = setTimeout(expire, BURN_IN_TIMEOUT_MS);
    // The combine reports nothing yet, so its watchdog never rearms; it is
    // here because `PendingRequest` is one shape for both jobs, and a second
    // shape for the sake of one unused field is the worse trade.
    const entry: PendingRequest = {
      resolve,
      reject,
      timer,
      restartTimer: () => {
        clearTimeout(entry.timer);
        entry.timer = setTimeout(expire, BURN_IN_TIMEOUT_MS);
      },
    };
    pending.set(id, entry);
    getWorker().postMessage({ id, type: 'COMBINE', parts }, parts);
  });
}

/**
 * Tear down the burn-in worker (releases the ~10MB WASM heap). Useful when
 * the document editor unmounts and we no longer need burn-in capabilities.
 */
export function disposeBurnInWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  rejectAllPending('Burn-in worker disposed');
}
